import type { HotelSearchResult } from 'tripPlanner/api/types'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import TextLink from 'LuxKit/TextLink'
import { capitalise } from 'lib/string/stringUtils'
import useCustomerRatingOptions from 'hooks/Search/useCustomerRatingOptions'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { hasHotelSearchFilters } from 'tripPlanner/api/hotelSearchFilters'
import HotelListingFilterDropdown from './HotelListingFilterDropdown'

interface Props {
  filters: App.OfferListFilters
  options: NonNullable<HotelSearchResult['filterOptions']>
  onChange: (filters: App.OfferListFilters) => void
  onClear: () => void
}
function HotelListingSearchFilters({ filters, options, onChange, onClear }: Props) {
  const intl = useIntl()
  const isTabletUp = useScreenSizeUp('tablet')
  const showClearAll = isTabletUp || hasHotelSearchFilters(filters)
  const { CUSTOMER_RATING_GTE } = useCustomerRatingOptions()
  const ratingOptions = useMemo(() => [CUSTOMER_RATING_GTE.EXCEPTIONAL, CUSTOMER_RATING_GTE.VERY_GOOD, CUSTOMER_RATING_GTE.GOOD, CUSTOMER_RATING_GTE.ANY_RATING].map(option => ({ value: option.value.toString(), label: option.label })), [CUSTOMER_RATING_GTE])
  const inclusionOptions = useMemo(() => options.inclusions.map(option => ({ value: option.slug, label: capitalise(option.label) })), [options.inclusions])
  const amenityOptions = useMemo(() => options.amenities.map(option => ({ value: option.slug, label: capitalise(option.label) })), [options.amenities])
  const ratings = useMemo(() => filters.customerRatingGte ? [filters.customerRatingGte.toString()] : [], [filters.customerRatingGte])
  const inclusions = useMemo(() => filters.inclusions ?? [], [filters.inclusions])
  const amenities = useMemo(() => filters.amenities ?? [], [filters.amenities])
  const applyRating = useCallback((values: Array<string>) => onChange({ ...filters, customerRatingGte: Number(values[0]) || undefined }), [filters, onChange])
  const applyInclusions = useCallback((values: Array<string>) => onChange({ ...filters, inclusions: values }), [filters, onChange])
  const applyAmenities = useCallback((values: Array<string>) => onChange({ ...filters, amenities: values }), [filters, onChange])
  const ratingTitle = intl.formatMessage({
    id: '4yw70FUuo3uvmkHReJoy',
    defaultMessage: 'Rating',
    description: 'Label for the customer rating filter in the Trip Planner hotel search drawer.',
  })
  const ratingLabel = intl.formatMessage({
    id: 'M4DFSpjYpDeV6qDcflMe',
    defaultMessage: '{count, plural, =0 {Rating} one {Rating (#)} other {Rating (#)}}',
    description: 'Label for the customer rating filter in the Trip Planner hotel search drawer.',
  }, { count: ratings.length })
  const inclusionsTitle = intl.formatMessage({
    id: 'tqDbsOKdYXpuvP1AojHt',
    defaultMessage: 'Inclusions',
    description: 'Label for the package inclusions filter in the Trip Planner hotel search drawer.',
  })
  const inclusionsLabel = intl.formatMessage({
    id: 'C-G8HvmnZVpjzbNBIhJ9',
    defaultMessage: '{count, plural, =0 {Inclusions} one {Inclusions (#)} other {Inclusions (#)}}',
    description: 'Label for the package inclusions filter in the Trip Planner hotel search drawer.',
  }, { count: inclusions.length })
  const amenitiesTitle = intl.formatMessage({
    id: '2LBHB-Hhn-9HZ-3ZlYIn',
    defaultMessage: 'Amenities',
    description: 'Label for the hotel amenities filter in the Trip Planner hotel search drawer.',
  })
  const amenitiesLabel = intl.formatMessage({
    id: 'IPDh_cUOf9Tp1NjpKCHk',
    defaultMessage: '{count, plural, =0 {Amenities} one {Amenities (#)} other {Amenities (#)}}',
    description: 'Label for the hotel amenities filter in the Trip Planner hotel search drawer.',
  }, { count: amenities.length })
  const clearLabel = intl.formatMessage({
    id: 'ko-rzg8v7vOz2zbl76XL',
    defaultMessage: 'Clear all',
    description: 'Button to clear dates, travellers and other filters in the Trip Planner hotel drawer.',
  })
  return (
    <>
      <HotelListingFilterDropdown single label={ratingTitle} chipLabel={ratingLabel} options={ratingOptions} selectedValues={ratings} onApply={applyRating} />
      <HotelListingFilterDropdown label={inclusionsTitle} chipLabel={inclusionsLabel} options={inclusionOptions} selectedValues={inclusions} onApply={applyInclusions} />
      <HotelListingFilterDropdown label={amenitiesTitle} chipLabel={amenitiesLabel} options={amenityOptions} selectedValues={amenities} onApply={applyAmenities} />
      {showClearAll && <Group direction="horizontal" gap={12}>
        {isTabletUp && <Divider direction="vertical" kind="primary" />}
        <TextLink size="medium" weight="bold" onClick={onClear}>{clearLabel}</TextLink>
      </Group>}
    </>
  )
}
export default HotelListingSearchFilters
